(function() {
  var HEVC_MIME = 'video/mp4; codecs="hvc1"';
  var H264_MIME = 'video/mp4; codecs="avc1.42E01E"';
  var testVideoEl = null;

  function canPlay(mime) {
    if (!testVideoEl) {
      testVideoEl = document.createElement('video');
    }
    if (!testVideoEl || typeof testVideoEl.canPlayType !== 'function') {
      return false;
    }
    var result = testVideoEl.canPlayType(mime) || '';
    return result !== 'no' && result !== '';
  }

  var SUPPORTS_HEVC = canPlay(HEVC_MIME);
  var SUPPORTS_H264 = canPlay(H264_MIME);

  function initUGCSlider(sectionEl) {
    if (!sectionEl || sectionEl.dataset.initialized === 'true') {
      return;
    }
    sectionEl.dataset.initialized = 'true';

    const track = sectionEl.querySelector('.ugc-slider__track');
    const viewport = sectionEl.querySelector('.ugc-slider__viewport');
    if (!track || !viewport) return;

    const supportsIntersectionObserver = typeof IntersectionObserver === 'function';
    const preventMediaDrag = function(event) {
      if (event.target.closest('.ugc-card')) {
        event.preventDefault();
      }
    };
    let previewObserver = supportsIntersectionObserver ? new IntersectionObserver(handlePreviewIntersection, {
      root: viewport,
      threshold: 0,
      rootMargin: '40px'
    }) : null;
    const observedPreviews = supportsIntersectionObserver ? new WeakSet() : null;
    let isSectionVisible = true;

    track.addEventListener('dragstart', preventMediaDrag);

    let baseCards = Array.from(track.querySelectorAll('.ugc-card'));
    if (!baseCards.length) return;

    track.querySelectorAll('img, video').forEach(function(mediaEl) {
      mediaEl.setAttribute('draggable', 'false');
    });

    const computed = window.getComputedStyle(sectionEl);
    const gapValue = parseFloat(computed.getPropertyValue('--ugc-gap')) || 0;
    const autoSpeed = parseFloat(sectionEl.dataset.autoSpeed || '0');

    baseCards.forEach(function(card, index) {
      card.dataset.base = 'true';
      card.dataset.cardIndex = String(index);
    });

    const mediaCount = baseCards.length;
    let mediaWidth = baseCards[0] ? baseCards[0].getBoundingClientRect().width : 0;
    let mediaHeight = baseCards[0] ? baseCards[0].getBoundingClientRect().height : 0;

    const templateCards = baseCards.map(function(card) {
      const clone = card.cloneNode(true);
      cleanCardNode(clone);
      clone.dataset.cardIndex = card.dataset.cardIndex || '';
      return clone;
    });

    let groupsState = [];
    let groupSpan = 0;
    let totalSpan = 0;
    let usingVariableSpans = false;
    let position = 0;
    let currentSpeed = autoSpeed ? -autoSpeed : 0;
    let targetSpeed = currentSpeed;
    const ease = 0.08;
    const TOUCH_ACTION_DEFAULT = 'pan-y pinch-zoom';
    const TOUCH_ACTION_LOCKED = 'none';
    let pointerActive = false;
    let pointerDragging = false;
    let pointerId = null;
    let pointerStartX = 0;
    let lastPointerX = 0;
    let pointerStartY = 0;
    let lastPointerY = 0;
    let pointerCaptured = false;
    let pointerLastTime = 0;
    let pointerVelocityX = 0;
    let pointerTotalTravelX = 0;
    let momentumVelocity = 0;
    const MOMENTUM_DECAY = 0.92;
    const MOMENTUM_EPSILON = 20;
    const MOMENTUM_MAX = 2400;
    let visibilityHandler = null;
    let previewsHiddenByDocument = false;
    let scrollLock = null;
    let suppressClicksUntil = 0;
    const pauseReasons = new Set();
    let rafId = null;
    let lastFrameTime = null;
    let activeFullCard = null;
    let activeFullVideo = null;
    let fullObserver = null;
    let wheelResumeTimer = null;
    let sectionObserver = null;
    let pageShowHandler = null;
    let lastViewportWidth = viewport.getBoundingClientRect().width || sectionEl.offsetWidth || 0;
    let resizeTimer = null;

    setTrackTouchAction(TOUCH_ACTION_DEFAULT);

    const preventTouchScroll = function(event) {
      if (!pointerDragging) return;
      if (event.cancelable) {
        event.preventDefault();
      }
    };

    buildGroups();
    preparePreviewVideos(sectionEl);
    queueInitialPreviewAutoplay();
    updateTargetSpeed();
    applyGroupTransforms();

    const prefersHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    if (prefersHover.matches) {
      sectionEl.addEventListener('mouseenter', function() { addPause('hover'); });
      sectionEl.addEventListener('mouseleave', function() { removePause('hover'); });
      viewport.addEventListener('wheel', handleWheel, { passive: false });
    }

    sectionObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          isSectionVisible = true;
          removePause('visibility');
          return;
        }

        const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
        const rootBottom = entry.rootBounds ? entry.rootBounds.bottom : (window.innerHeight || document.documentElement.clientHeight || 0);
        const fullyAbove = entry.boundingClientRect.bottom <= rootTop;
        const fullyBelow = entry.boundingClientRect.top >= rootBottom;
        const fullyOut = fullyAbove || fullyBelow || entry.intersectionRatio <= 0;

        if (fullyOut) {
          isSectionVisible = false;
          addPause('visibility');
          stopFullVideo();
        }
      });
    }, { threshold: 0.0, rootMargin: '0px' });
    sectionObserver.observe(sectionEl);

    visibilityHandler = function() {
      if (document.hidden) {
        handleDocumentHidden();
      } else {
        handleDocumentVisible();
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
    pageShowHandler = function(event) {
      if (event && event.persisted !== true) {
        return;
      }
      if (document.visibilityState === 'hidden') {
        return;
      }
      if (previewsHiddenByDocument) {
        handleDocumentVisible();
      } else {
        startVisiblePreviewPlayback();
      }
    };
    window.addEventListener('pageshow', pageShowHandler);
    if (document.hidden) {
      handleDocumentHidden();
    }

    track.addEventListener('pointerdown', handlePointerDown);
    track.addEventListener('pointermove', handlePointerMove);
    track.addEventListener('pointerup', handlePointerUp);
    track.addEventListener('pointercancel', handlePointerUp);
    sectionEl.addEventListener('touchmove', preventTouchScroll, { passive: false });

    track.addEventListener('click', function(event) {
      if (suppressClicksUntil && getNow() < suppressClicksUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const playBtn = event.target.closest('.ugc-card__play');
      if (playBtn) {
        event.preventDefault();
        const card = playBtn.closest('.ugc-card');
        toggleFullVideo(card);
        return;
      }
      const previewVideoEl = event.target.closest('.ugc-card__video-preview');
      if (previewVideoEl) {
        const card = previewVideoEl.closest('.ugc-card');
        if (card && card.dataset.hasFull === 'true') {
          event.preventDefault();
          toggleFullVideo(card);
        }
        return;
      }
      const posterEl = event.target.closest('.ugc-card__poster');
      if (posterEl) {
        const card = posterEl.closest('.ugc-card');
        if (card && card.dataset.hasFull === 'true' && card.dataset.hasPreview !== 'true') {
          event.preventDefault();
          toggleFullVideo(card);
          return;
        }
      }
      const wrapperEl = event.target.closest('.ugc-card__video-wrapper');
      if (wrapperEl) {
        const card = wrapperEl.closest('.ugc-card');
        if (card && card.dataset.hasFull === 'true' && card.dataset.hasPreview !== 'true') {
          event.preventDefault();
          toggleFullVideo(card);
          return;
        }
      }
      const fullVideoEl = event.target.closest('.ugc-card__video-full');
      if (fullVideoEl && activeFullCard) {
        const targetCard = fullVideoEl.closest('.ugc-card');
        if (targetCard) {
          event.preventDefault();
          stopFullVideo();
        }
      }
    });

    sectionEl.addEventListener('keydown', function(event) {
      if (event.key === 'Escape' && activeFullCard) {
        stopFullVideo();
      }
    });

    window.addEventListener('resize', handleResize);
    ensureLoopRunning();

    sectionEl._ugcCleanup = function() {
      stopFullVideo();
      pauseReasons.clear();
      stopLoop();
      window.removeEventListener('resize', handleResize);
      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
        visibilityHandler = null;
      }
      if (pageShowHandler) {
        window.removeEventListener('pageshow', pageShowHandler);
        pageShowHandler = null;
      }
      if (wheelResumeTimer) {
        clearTimeout(wheelResumeTimer);
        wheelResumeTimer = null;
      }
      if (prefersHover.matches) {
        viewport.removeEventListener('wheel', handleWheel);
      }
      if (fullObserver) {
        fullObserver.disconnect();
        fullObserver = null;
      }
      if (previewObserver) {
        previewObserver.disconnect();
        previewObserver = null;
      }
      if (sectionObserver) {
        sectionObserver.disconnect();
        sectionObserver = null;
      }
      if (resizeTimer) {
        clearTimeout(resizeTimer);
        resizeTimer = null;
      }
      track.removeEventListener('dragstart', preventMediaDrag);
      sectionEl.removeEventListener('touchmove', preventTouchScroll);
      sectionEl.classList.remove('ugc--is-dragging');
      setTrackTouchAction(TOUCH_ACTION_DEFAULT);
      scrollLock = null;
      pointerActive = false;
      pointerDragging = false;
      pointerTotalTravelX = 0;
      pointerVelocityX = 0;
      momentumVelocity = 0;
      suppressClicksUntil = 0;
      groupsState = [];
      previewsHiddenByDocument = false;
    };

    function handleResize() {
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = setTimeout(applyResize, 160);
    }

    function applyResize() {
      resizeTimer = null;
      const newViewportWidth = viewport.getBoundingClientRect().width || sectionEl.offsetWidth || 0;
      if (Math.abs(newViewportWidth - lastViewportWidth) < 1) {
        return;
      }
      lastViewportWidth = newViewportWidth;
      const sampleCard = groupsState[0]?.el?.querySelector('.ugc-card');
      if (sampleCard) {
        const rect = sampleCard.getBoundingClientRect();
        mediaWidth = rect.width;
        mediaHeight = rect.height;
      }
      stopFullVideo();
      buildGroups();
      preparePreviewVideos(sectionEl);
      queueInitialPreviewAutoplay();
      applyGroupTransforms();
      updateTargetSpeed();
    }

    function handlePointerDown(event) {
      if (typeof event.button !== 'undefined' && event.button !== 0) {
        return;
      }
      ensureLoopRunning();
      pointerActive = true;
      pointerDragging = false;
      pointerId = event.pointerId;
      pointerStartX = event.clientX;
      lastPointerX = event.clientX;
      pointerStartY = event.clientY;
      lastPointerY = event.clientY;
      pointerLastTime = getNow();
      pointerVelocityX = 0;
      pointerTotalTravelX = 0;
      momentumVelocity = 0;
      removePause('momentum');
      scrollLock = null;
      sectionEl.classList.remove('ugc--is-dragging');
      setTrackTouchAction(TOUCH_ACTION_DEFAULT);
      track.classList.add('is-pointer-down');
    }

    function handlePointerMove(event) {
      if (!pointerActive || event.pointerId !== pointerId) return;
      const deltaX = event.clientX - lastPointerX;
      const deltaY = event.clientY - lastPointerY;
      const now = getNow();
      const deltaTime = Math.max((now - pointerLastTime) / 1000, 0.001);
      pointerLastTime = now;
      lastPointerX = event.clientX;
      lastPointerY = event.clientY;

      if (!pointerDragging) {
        const totalDeltaX = event.clientX - pointerStartX;
        const totalDeltaY = event.clientY - pointerStartY;
        pointerTotalTravelX = Math.abs(totalDeltaX);
        if (Math.abs(totalDeltaX) > 6 && Math.abs(totalDeltaX) > Math.abs(totalDeltaY)) {
          pointerDragging = true;
          addPause('drag');
          track.setPointerCapture(pointerId);
          pointerCaptured = true;
          scrollLock = 'horizontal';
          pointerVelocityX = 0;
          momentumVelocity = 0;
          sectionEl.classList.add('ugc--is-dragging');
          setTrackTouchAction(TOUCH_ACTION_LOCKED);
          if (event.pointerType === 'touch' && event.cancelable) {
            event.preventDefault();
          }
        } else if (Math.abs(totalDeltaY) > 6 && Math.abs(totalDeltaY) > Math.abs(totalDeltaX)) {
          scrollLock = 'vertical';
          track.classList.remove('is-pointer-down');
          pointerActive = false;
          pointerDragging = false;
          pointerId = null;
          if (pointerCaptured) {
            track.releasePointerCapture(event.pointerId);
            pointerCaptured = false;
          }
          sectionEl.classList.remove('ugc--is-dragging');
          setTrackTouchAction(TOUCH_ACTION_DEFAULT);
          pointerVelocityX = 0;
          pointerTotalTravelX = 0;
          momentumVelocity = 0;
          removePause('momentum');
          scrollLock = null;
          return;
        }
      } else if (scrollLock !== 'horizontal' && Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 2) {
        // Switch to vertical if user changes intent mid-gesture
        scrollLock = 'vertical';
        track.classList.remove('is-pointer-down');
        pointerActive = false;
        pointerDragging = false;
        pointerId = null;
        if (pointerCaptured) {
          track.releasePointerCapture(event.pointerId);
          pointerCaptured = false;
        }
        sectionEl.classList.remove('ugc--is-dragging');
        setTrackTouchAction(TOUCH_ACTION_DEFAULT);
        pointerVelocityX = 0;
        pointerTotalTravelX = 0;
        momentumVelocity = 0;
        removePause('momentum');
        scrollLock = null;
        return;
      }

      if (pointerDragging) {
        if (event.pointerType === 'touch' && event.cancelable) {
          event.preventDefault();
        }
        position += deltaX;
        normalizePosition();
        applyGroupTransforms();
        pointerVelocityX = deltaX / deltaTime;
        pointerTotalTravelX += Math.abs(deltaX);
      }
    }

    function handlePointerUp(event) {
      if (!pointerActive || event.pointerId !== pointerId) return;
      track.classList.remove('is-pointer-down');

      if (pointerDragging) {
        removePause('drag');
        const flickVelocity = Math.max(Math.min(pointerVelocityX, MOMENTUM_MAX), -MOMENTUM_MAX);
        if (Math.abs(flickVelocity) > MOMENTUM_EPSILON) {
          momentumVelocity = flickVelocity;
          addPause('momentum');
          ensureLoopRunning();
        } else {
          momentumVelocity = 0;
          removePause('momentum');
          stopLoopIfIdle();
        }
        if (pointerTotalTravelX > 12) {
          suppressClicksUntil = getNow() + 350;
        }
      } else {
        momentumVelocity = 0;
        removePause('momentum');
        stopLoopIfIdle();
      }

      pointerActive = false;
      pointerDragging = false;
      pointerId = null;
      if (pointerCaptured) {
        track.releasePointerCapture(event.pointerId);
        pointerCaptured = false;
      }
      scrollLock = null;
      pointerVelocityX = 0;
      pointerTotalTravelX = 0;
      sectionEl.classList.remove('ugc--is-dragging');
      setTrackTouchAction(TOUCH_ACTION_DEFAULT);
    }

    function handleWheel(event) {
      if (!groupSpan || !groupsState.length) return;
      const absX = Math.abs(event.deltaX);
      const absY = Math.abs(event.deltaY);
      const delta = event.deltaX;
      const horizontalIntent = absX > absY * 1.1;

      if (!horizontalIntent) {
        if (scrollLock === 'horizontal' && !pointerActive && !pointerDragging) {
          scrollLock = null;
          sectionEl.classList.remove('ugc--is-dragging');
          setTrackTouchAction(TOUCH_ACTION_DEFAULT);
        }
        return;
      }

      if (!delta) {
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      scrollLock = 'horizontal';
      sectionEl.classList.add('ugc--is-dragging');
      setTrackTouchAction(TOUCH_ACTION_LOCKED);
      addPause('wheel');
      position -= delta;
      normalizePosition();
      applyGroupTransforms();
      if (wheelResumeTimer) clearTimeout(wheelResumeTimer);
      wheelResumeTimer = setTimeout(function() {
        removePause('wheel');
        if (!pointerActive && !pointerDragging) {
          scrollLock = null;
          sectionEl.classList.remove('ugc--is-dragging');
          setTrackTouchAction(TOUCH_ACTION_DEFAULT);
        }
      }, 160);
    }

    function buildGroups() {
      groupsState = [];
      totalSpan = 0;
      track.innerHTML = '';

      const sectionWidth = viewport.getBoundingClientRect().width || 0;
      lastViewportWidth = sectionWidth || lastViewportWidth;
      if (!mediaWidth) {
        const sample = baseCards[0];
        if (sample) {
          const rect = sample.getBoundingClientRect();
          mediaWidth = rect.width;
          mediaHeight = rect.height;
        } else {
          mediaWidth = parseFloat(computed.getPropertyValue('--ugc-media-width')) || 1;
        }
      }

      const sequenceUnit = (mediaWidth + gapValue) * Math.max(1, mediaCount);
      const duplicationRaw = sequenceUnit ? (sectionWidth * 0.55) / sequenceUnit : 0;
      sectionEl.dataset.ugcDupRatio = duplicationRaw ? duplicationRaw.toFixed(4) : '0';
      const groupBlueprints = [];

      if (duplicationRaw < 0.5) {
        const evenMediaCount = mediaCount >= 2 ? (mediaCount % 2 === 0 ? mediaCount : mediaCount - 1) : mediaCount;
        const divisor = Math.max(1, evenMediaCount > 0 ? evenMediaCount : mediaCount);
        const secondaryDenominator = (mediaWidth + gapValue) * divisor;
        const secondaryNumerator = sectionWidth * 0.55;
        const secondaryRatio = secondaryDenominator ? (secondaryNumerator / secondaryDenominator) : 0;
        sectionEl.dataset.ugcSecondaryRatio = secondaryRatio ? secondaryRatio.toFixed(4) : '0';
        const shouldSplitSequences = secondaryRatio < 0.25 && mediaCount > 1;
        sectionEl.dataset.ugcSequenceMode = shouldSplitSequences ? 'split' : 'full';
        sectionEl.dataset.ugcSplitActive = shouldSplitSequences ? 'true' : 'false';
        usingVariableSpans = shouldSplitSequences;

        if (shouldSplitSequences) {
          const splitIndex = Math.ceil(templateCards.length / 2);
          const firstHalf = templateCards.slice(0, splitIndex);
          let secondHalf = templateCards.slice(splitIndex);
          if (!secondHalf.length) {
            secondHalf = templateCards.slice();
          }
          groupBlueprints.push({ sequences: [firstHalf], keyBase: 'split-0' });
          groupBlueprints.push({ sequences: [secondHalf], keyBase: 'split-1' });
        } else {
          usingVariableSpans = false;
          groupBlueprints.push({ sequences: [templateCards], keyBase: 'full-0' });
          groupBlueprints.push({ sequences: [templateCards], keyBase: 'full-1' });
        }
      } else {
        sectionEl.dataset.ugcSequenceMode = 'duplicate';
        sectionEl.dataset.ugcSplitActive = 'false';
        usingVariableSpans = false;
        const sequencesPerGroup = Math.ceil(Math.max(1, duplicationRaw));
        for (let gi = 0; gi < 3; gi += 1) {
          const sequences = [];
          for (let seq = 0; seq < sequencesPerGroup; seq += 1) {
            sequences.push(templateCards);
          }
          groupBlueprints.push({ sequences: sequences, keyBase: 'seq-' + gi });
        }
      }

      const groupCount = groupBlueprints.length;
      const fragment = document.createDocumentFragment();
      const newStates = [];
      const indexOffset = Math.floor(groupCount / 2);

      groupBlueprints.forEach(function(blueprint, gi) {
        const groupEl = document.createElement('div');
        groupEl.className = 'ugc-slider__group';
        groupEl.style.gap = gapValue + 'px';
        groupEl.style.left = '0';
        groupEl.dataset.groupIndex = String(gi);

        const sequencesForGroup = blueprint.sequences && blueprint.sequences.length ? blueprint.sequences : [templateCards];
        sequencesForGroup.forEach(function(sequenceTemplates, seqIndex) {
          const templatesForSequence = sequenceTemplates && sequenceTemplates.length ? sequenceTemplates : templateCards;
          const keyPrefix = (blueprint.keyBase ? blueprint.keyBase + '-' : 'seq-' + gi + '-') + String(seqIndex);
          appendTemplatesToGroup(groupEl, templatesForSequence, gi, seqIndex, keyPrefix);
        });

        fragment.appendChild(groupEl);
        newStates.push({
          el: groupEl,
          index: gi - indexOffset,
          sequenceId: gi,
          isDuplicate: gi > 0
        });
      });

      track.appendChild(fragment);

      groupsState = newStates;

      if (usingVariableSpans) {
        groupSpan = totalSpan || (mediaWidth + gapValue) * Math.max(1, mediaCount);
        let maxHeight = mediaHeight;
        groupsState.forEach(function(state) {
          const rect = state.el.getBoundingClientRect();
          if (rect && rect.height) {
            maxHeight = Math.max(maxHeight, rect.height);
          }
        });
        if (maxHeight) {
          mediaHeight = maxHeight;
          track.style.height = maxHeight + 'px';
        }
        track.style.position = 'relative';
        track.style.display = 'block';
        reindexGroups();
        groupSpan = totalSpan || groupSpan;
        position = 0;
        applyGroupTransforms();
        return;
      }

      const firstGroupRect = newStates[0]?.el.getBoundingClientRect();
      const fallbackCardCount = (groupBlueprints[0]?.sequences || []).reduce(function(total, seqTemplates) {
        return total + (seqTemplates ? seqTemplates.length : 0);
      }, 0) || mediaCount;
      groupSpan = firstGroupRect ? firstGroupRect.width + gapValue : (mediaWidth + gapValue) * Math.max(1, fallbackCardCount);
      if (!groupSpan) {
        groupSpan = (mediaWidth + gapValue) * Math.max(1, fallbackCardCount);
      }

      const groupHeight = firstGroupRect?.height || mediaHeight;
      if (groupHeight) {
        mediaHeight = groupHeight;
        track.style.height = groupHeight + 'px';
      }
      track.style.position = 'relative';
      track.style.display = 'block';

      totalSpan = groupSpan * Math.max(1, groupCount);

      reindexGroups();
      position = 0;
      applyGroupTransforms();
    }

    function appendTemplatesToGroup(groupEl, templates, groupIndex, sequenceIndex, keyPrefix) {
      const sequenceLabel = String(sequenceIndex);
      const keyLabel = String(keyPrefix);
      templates.forEach(function(template, localIndex) {
        const cardClone = template.cloneNode(true);
        cleanCardNode(cardClone);
        cardClone.dataset.sequenceIndex = sequenceLabel;
        cardClone.dataset.groupIndex = String(groupIndex);
        const baseKey = cardClone.dataset.cardIndex ? cardClone.dataset.cardIndex + '-' : '';
        cardClone.dataset.sequenceKey = baseKey + keyLabel + '-' + String(localIndex);
        groupEl.appendChild(cardClone);
      });
    }

    function updateDynamicMetrics() {
      if (!groupsState.length) {
        totalSpan = 0;
        return;
      }
      let cumulative = 0;
      let newTotal = 0;
      groupsState.forEach(function(state) {
        const rect = state.el.getBoundingClientRect();
        const width = rect && rect.width ? rect.width : (mediaWidth + gapValue) * Math.max(1, state.el.children.length || 1);
        state.width = width;
        state.span = width + gapValue;
        state.offset = cumulative;
        cumulative += state.span;
        newTotal += state.span;
      });
      totalSpan = newTotal || (mediaWidth + gapValue) * Math.max(1, mediaCount);
    }

    function cleanCardNode(clone) {
      clone.classList.add('ugc-card--clone');
      clone.removeAttribute('data-base');
      clone.removeAttribute('data-block-id');
      clone.removeAttribute('data-block-order');
      clone.removeAttribute('data-shopify-editor-block-id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[data-block-id]').forEach(function(el) { el.removeAttribute('data-block-id'); });
      clone.querySelectorAll('[data-block-order]').forEach(function(el) { el.removeAttribute('data-block-order'); });
      clone.querySelectorAll('[data-shopify-editor-block-id]').forEach(function(el) { el.removeAttribute('data-shopify-editor-block-id'); });
      clone.querySelectorAll('[data-shopify-editor-block]').forEach(function(el) { el.removeAttribute('data-shopify-editor-block'); });
    }

    function addPause(reason) {
      if (!pauseReasons.has(reason)) {
        pauseReasons.add(reason);
        updateTargetSpeed();
      }
    }

    function removePause(reason) {
      if (pauseReasons.delete(reason)) {
        updateTargetSpeed();
      }
    }

    function updateTargetSpeed() {
      if (!autoSpeed) {
        currentSpeed = 0;
        targetSpeed = 0;
        stopLoopIfIdle();
        return;
      }
      const paused = pauseReasons.size > 0;
      targetSpeed = paused ? 0 : -autoSpeed;
      if (!paused) {
        ensureLoopRunning();
      } else {
        stopLoopIfIdle();
      }
    }

    function ensureLoopRunning() {
      if (rafId) {
        return;
      }
      lastFrameTime = null;
      rafId = requestAnimationFrame(step);
    }

    function stopLoop() {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      lastFrameTime = null;
      momentumVelocity = 0;
      removePause('momentum');
      sectionEl.classList.remove('ugc--is-dragging');
      setTrackTouchAction(TOUCH_ACTION_DEFAULT);
    }

    function shouldContinueLoop() {
      if (autoSpeed && pauseReasons.size === 0) return true;
      if (Math.abs(currentSpeed) > 0.01) return true;
      if (Math.abs(momentumVelocity) > 1) return true;
      if (pointerActive || pointerDragging) return true;
      return false;
    }

    function stopLoopIfIdle() {
      if (!rafId) return;
      if (shouldContinueLoop()) return;
      cancelAnimationFrame(rafId);
      rafId = null;
      lastFrameTime = null;
    }

    function step(timestamp) {
      if (!lastFrameTime) {
        lastFrameTime = timestamp;
      }
      const delta = (timestamp - lastFrameTime) / 1000;
      lastFrameTime = timestamp;

      if (autoSpeed) {
        currentSpeed += (targetSpeed - currentSpeed) * ease;
        if (Math.abs(currentSpeed) < 0.001) {
          currentSpeed = 0;
        }
      }

      let appliedSpeed = currentSpeed;
      if (momentumVelocity) {
        appliedSpeed += momentumVelocity;
        const decay = Math.pow(MOMENTUM_DECAY, delta * 60);
        momentumVelocity *= decay;
        if (Math.abs(momentumVelocity) < MOMENTUM_EPSILON) {
          momentumVelocity = 0;
          removePause('momentum');
        }
      }

      position += appliedSpeed * delta;

      normalizePosition();
      applyGroupTransforms();

      if (shouldContinueLoop()) {
        rafId = requestAnimationFrame(step);
      } else {
        rafId = null;
        lastFrameTime = null;
      }
    }

    function normalizePosition() {
      if (!groupsState.length) return;
      if (usingVariableSpans) {
        normalizePositionDynamic();
        maintainFullVideoState();
        return;
      }
      if (!groupSpan) return;
      while (position <= -groupSpan) {
        position += groupSpan;
        shiftGroupsLeft();
        maintainFullVideoState();
      }
      while (position >= groupSpan) {
        position -= groupSpan;
        shiftGroupsRight();
        maintainFullVideoState();
      }
    }

    function normalizePositionDynamic() {
      if (!groupsState.length) return;
      let firstSpan = groupsState[0]?.span || 0;
      while (firstSpan && position <= -firstSpan) {
        position += firstSpan;
        shiftGroupsLeft();
        firstSpan = groupsState[0]?.span || 0;
      }
      let lastSpan = groupsState[groupsState.length - 1]?.span || 0;
      while (lastSpan && position >= lastSpan) {
        position -= lastSpan;
        shiftGroupsRight();
        lastSpan = groupsState[groupsState.length - 1]?.span || 0;
      }
    }

    function applyGroupTransforms() {
      if (!groupsState.length) return;
      if (usingVariableSpans) {
        applyGroupTransformsDynamic();
        return;
      }
      groupsState.forEach(function(state) {
        const x = (state.index * groupSpan) + position;
        state.el.style.transform = 'translate3d(' + x + 'px, 0, 0)';
      });
      rebalanceGroups();
      groupsState.forEach(function(state) {
        const x = (state.index * groupSpan) + position;
        state.el.style.transform = 'translate3d(' + x + 'px, 0, 0)';
      });
    }

    function applyGroupTransformsDynamic() {
      if (!totalSpan) return;
      let centerOffset = totalSpan / 2;
      groupsState.forEach(function(state) {
        const x = position + (state.offset || 0) - centerOffset;
        state.el.style.transform = 'translate3d(' + x + 'px, 0, 0)';
      });
      rebalanceGroupsDynamic();
      centerOffset = totalSpan / 2;
      groupsState.forEach(function(state) {
        const x = position + (state.offset || 0) - centerOffset;
        state.el.style.transform = 'translate3d(' + x + 'px, 0, 0)';
      });
    }

    function rebalanceGroupsDynamic() {
      const count = groupsState.length;
      if (!count) {
        return;
      }
      if (count !== 2) {
        updateDynamicMetrics();
        return;
      }

      const viewportRect = viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + viewportRect.width / 2;
      const entries = groupsState.map(function(state) {
        const rect = state.el.getBoundingClientRect();
        return {
          state: state,
          rect: rect,
          center: rect.left + rect.width / 2,
          translate: extractTranslateX(state.el)
        };
      });

      const primaryEntry = entries.reduce(function(closest, current) {
        const closestDelta = Math.abs(closest.center - viewportCenter);
        const currentDelta = Math.abs(current.center - viewportCenter);
        return currentDelta < closestDelta ? current : closest;
      }, entries[0]);

      const duplicateEntry = entries.find(function(entry) {
        return entry !== primaryEntry;
      }) || primaryEntry;

      if (!primaryEntry || !duplicateEntry) {
        updateDynamicMetrics();
        return;
      }

      const deltaToCenter = primaryEntry.center - viewportCenter;
      const centerDeadzone = 8;
      if (Math.abs(deltaToCenter) <= centerDeadzone) {
        updateDynamicMetrics();
        return;
      }

      const primaryLeftOfCenter = deltaToCenter < 0;
      const desiredOrder = primaryLeftOfCenter
        ? [primaryEntry.state, duplicateEntry.state]
        : [duplicateEntry.state, primaryEntry.state];

      if (desiredOrder[0] === groupsState[0] && desiredOrder[1] === groupsState[1]) {
        updateDynamicMetrics();
        return;
      }

      const preservedTranslate = primaryEntry.translate;
      groupsState = desiredOrder;
      reindexGroups();
      syncPositionToDynamicState(primaryEntry.state, preservedTranslate);
    }

    function syncPositionToDynamicState(state, translateOverride) {
      if (!state || typeof translateOverride !== 'number' || !isFinite(translateOverride)) {
        updateDynamicMetrics();
        return;
      }
      const centerOffset = totalSpan ? totalSpan / 2 : 0;
      const offset = state.offset || 0;
      position = translateOverride - offset + centerOffset;
    }

    function shiftGroupsLeft() {
      const first = groupsState.shift();
      if (!first) return;
      groupsState.push(first);
      reindexGroups();
    }

    function shiftGroupsRight() {
      const last = groupsState.pop();
      if (!last) return;
      groupsState.unshift(last);
      reindexGroups();
    }

    function reindexGroups() {
      const count = groupsState.length;
      if (!count) return;
      if (usingVariableSpans) {
        groupsState.forEach(function(state, idx) {
          state.index = idx;
        });
        updateDynamicMetrics();
        return;
      }
      if (count % 2 === 1) {
        const midpoint = (count - 1) / 2;
        groupsState.forEach(function(state, idx) {
          state.index = idx - midpoint;
        });
      } else {
        const midpoint = count / 2;
        groupsState.forEach(function(state, idx) {
          state.index = idx - midpoint + 0.5;
        });
      }
    }

    function rebalanceGroups() {
      if (usingVariableSpans) {
        return;
      }
      const count = groupsState.length;
      if (!count) return;
      const viewportRect = viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + viewportRect.width / 2;

      const entries = groupsState.map(function(state) {
        const rect = state.el.getBoundingClientRect();
        return {
          state: state,
          rect: rect,
          center: rect.left + rect.width / 2
        };
      });

      if (count === 3) {
        const sorted = entries.slice().sort(function(a, b) {
          return a.center - b.center;
        });

        const centralEntry = sorted.reduce(function(closest, current) {
          const closestDelta = Math.abs(closest.center - viewportCenter);
          const currentDelta = Math.abs(current.center - viewportCenter);
          return currentDelta < closestDelta ? current : closest;
        }, sorted[0]);

        const centralIdx = sorted.indexOf(centralEntry);
        if (centralIdx !== -1) {
          const leftEntry = sorted[(centralIdx - 1 + count) % count];
          const rightEntry = sorted[(centralIdx + 1) % count];
          if (leftEntry && rightEntry) {
            groupsState = [leftEntry.state, centralEntry.state, rightEntry.state];
            reindexGroups();
            syncPositionToEntry(centralEntry);
            return;
          }
        }
      } else if (count === 2) {
        const primaryEntry = entries.reduce(function(closest, current) {
          const closestDelta = Math.abs(closest.center - viewportCenter);
          const currentDelta = Math.abs(current.center - viewportCenter);
          return currentDelta < closestDelta ? current : closest;
        }, entries[0]);
        const duplicateEntry = entries.find(function(entry) {
          return entry !== primaryEntry;
        }) || primaryEntry;

        if (primaryEntry && duplicateEntry) {
          const deltaToCenter = primaryEntry.center - viewportCenter;
          const centerDeadzone = 8; // pixels to avoid flicker near exact center
          if (Math.abs(deltaToCenter) <= centerDeadzone) {
            return; // keep current order until clearly left/right
          }
          const primaryLeftOfCenter = deltaToCenter < 0;
          groupsState = primaryLeftOfCenter
            ? [primaryEntry.state, duplicateEntry.state]
            : [duplicateEntry.state, primaryEntry.state];
          reindexGroups();
          syncPositionToEntry(primaryEntry);
          return;
        }
      }

      reindexGroups();
    }

    function syncPositionToEntry(entry) {
      if (!entry || !entry.state || !entry.state.el || !groupSpan) {
        return;
      }
      const translateX = extractTranslateX(entry.state.el);
      if (!isFinite(translateX)) {
        return;
      }
      position = translateX - (entry.state.index * groupSpan);
    }

    function extractTranslateX(element) {
      if (!element) return null;

      var translate = parseTranslateValue(element.style ? element.style.transform : '');
      if (translate !== null) return translate;

      if (window.getComputedStyle) {
        translate = parseTranslateValue(window.getComputedStyle(element).transform);
        if (translate !== null) return translate;
      }

      return 0;
    }

    function parseTranslateValue(transformValue) {
      if (!transformValue || transformValue === 'none') {
        return 0;
      }

      const translateMatch = transformValue.match(/translate3d\((-?\d+(?:\.\d+)?)px/);
      if (translateMatch) {
        return parseFloat(translateMatch[1]);
      }

      const matrixMatch = transformValue.match(/matrix\(([^)]+)\)/);
      if (matrixMatch) {
        const parts = matrixMatch[1].split(',');
        if (parts.length >= 6) {
          return parseFloat(parts[4]);
        }
      }

      const matrix3dMatch = transformValue.match(/matrix3d\(([^)]+)\)/);
      if (matrix3dMatch) {
        const parts = matrix3dMatch[1].split(',');
        if (parts.length >= 16) {
          return parseFloat(parts[12]);
        }
      }

      return null;
    }

    function maintainFullVideoState() {
      if (!activeFullCard || !activeFullVideo) return;
      const wrapper = activeFullCard.querySelector('.ugc-card__video-wrapper');
      if (!wrapper) {
        stopFullVideo();
        return;
      }

      const previewVideo = activeFullCard.querySelector('.ugc-card__video-preview');
      if (previewVideo && previewVideo.paused) {
        previewVideo.pause();
      }

      if (activeFullVideo.paused) {
        const playPromise = activeFullVideo.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.catch(function() {});
        }
      }
    }

    function handleDocumentHidden() {
      if (previewsHiddenByDocument) return;
      previewsHiddenByDocument = true;
      addPause('tab-hidden');
      if (activeFullCard) {
        stopFullVideo();
      }

      sectionEl.querySelectorAll('.ugc-card').forEach(function(card) {
        if (card.dataset.hasPreview !== 'true') return;
        const preview = card.querySelector('.ugc-card__video-preview');
        if (!preview) return;

        if (!preview.dataset.originalDisplay) {
          preview.dataset.originalDisplay = preview.style.display || '';
        }
        try {
          preview.pause();
          preview.currentTime = 0;
        } catch (error) {
          /* noop */
        }
        preview.style.display = 'none';

        const wrapper = card.querySelector('.ugc-card__video-wrapper');
        if (wrapper) {
          wrapper.classList.remove('is-ready');
          wrapper.classList.remove('playing-full');
        }
        card.classList.remove('ugc-card--active-full');
        card.classList.remove('ugc-card--preview-paused');
        card.classList.add('ugc-card--preview-hidden');
      });

      stopLoopIfIdle();
    }

    function handleDocumentVisible() {
      if (!previewsHiddenByDocument) return;
      previewsHiddenByDocument = false;

      sectionEl.querySelectorAll('.ugc-card').forEach(function(card) {
        if (card.dataset.hasPreview !== 'true') return;
        if (!card.classList.contains('ugc-card--preview-hidden')) return;

        const preview = card.querySelector('.ugc-card__video-preview');
        const wrapper = card.querySelector('.ugc-card__video-wrapper');

        if (preview) {
          preview.style.display = preview.dataset.originalDisplay || '';
          delete preview.dataset.originalDisplay;
          if (!card.classList.contains('ugc-card--preview-paused')) {
            ensurePreviewSource(preview);
            loadPreview(preview);
          }

          if (wrapper) {
            if (preview.readyState >= 2 && !card.classList.contains('ugc-card--preview-paused')) {
              wrapper.classList.add('is-ready');
            } else {
              const markReady = function() {
                wrapper.classList.add('is-ready');
              };
              preview.addEventListener('loadeddata', markReady, { once: true });
            }
          }
          if (!card.classList.contains('ugc-card--preview-paused')) {
            requestAnimationFrame(function() {
              playPreviewWhenReady(preview);
            });
          }
        }

        card.classList.remove('ugc-card--preview-hidden');
      });

      removePause('tab-hidden');
      updateTargetSpeed();
      resumePreviewVideos();
    }

    function preparePreviewVideos(root) {
      const previews = root.querySelectorAll('.ugc-card__video-preview');
      previews.forEach(function(video) {
        if (video.dataset.prepared === 'true') return;
        video.dataset.prepared = 'true';
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.autoplay = true;
        video.defaultMuted = true;
        video.setAttribute('autoplay', '');
        const wrapper = video.closest('.ugc-card__video-wrapper');
        const markReady = function() {
          if (wrapper) {
            wrapper.classList.add('is-ready');
          }
        };
        video.addEventListener('loadeddata', markReady, { once: true });
        if (previewObserver && observedPreviews) {
          observedPreviews.add(video);
          previewObserver.observe(video);
          if (isSectionVisible && isVideoInViewport(video)) {
            ensurePreviewSource(video);
            loadPreview(video);
            playPreviewWhenReady(video);
          }
        } else {
          ensurePreviewSource(video);
          loadPreview(video);
          playPreviewWhenReady(video);
        }
      });
    }

    function toggleFullVideo(card) {
      if (!card) return;
      if (card === activeFullCard) {
        stopFullVideo();
      } else {
        startFullVideo(card);
      }
    }

    function startFullVideo(card) {
      if (!card || card.dataset.hasFull !== 'true') return;
      const wrapper = card.querySelector('.ugc-card__video-wrapper');
      const fullVideo = card.querySelector('.ugc-card__video-full');
      if (!wrapper || !fullVideo) return;

      if (activeFullCard && activeFullCard !== card) {
        stopFullVideo();
      }

      activeFullCard = card;
      activeFullVideo = fullVideo;

      momentumVelocity = 0;
      removePause('momentum');
      sectionEl.classList.remove('ugc--is-dragging');
      setTrackTouchAction(TOUCH_ACTION_DEFAULT);

      addPause('full');
      sectionEl.classList.add('ugc--full-playing');
      card.classList.add('ugc-card--active-full');
      card.classList.remove('ugc-card--preview-paused');
      wrapper.classList.add('playing-full');

      const previewVideo = card.querySelector('.ugc-card__video-preview');
      if (previewVideo) {
        previewVideo.pause();
      }

      pauseOtherPreviews(card);

      if (fullObserver) {
        fullObserver.disconnect();
        fullObserver = null;
      }
      fullObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          const rootBounds = entry.rootBounds;
          const topLimit = rootBounds ? rootBounds.top : 0;
          const bottomLimit = rootBounds ? rootBounds.bottom : window.innerHeight || document.documentElement.clientHeight;
          const rect = entry.boundingClientRect;
          const verticallyVisible = rect.bottom > topLimit && rect.top < bottomLimit;
          if (!verticallyVisible) {
            stopFullVideo();
          }
        });
      }, { threshold: 0.0 });
      fullObserver.observe(fullVideo);

      ensureFullVideoSources(fullVideo);
      fullVideo.load();
      fullVideo.muted = false;
      fullVideo.loop = false;
      fullVideo.controls = false;
      fullVideo.currentTime = 0;

      const handleEnded = function() {
        fullVideo.removeEventListener('ended', handleEnded);
        stopFullVideo();
      };
      fullVideo._ugcHandleEnded = handleEnded;
      fullVideo.addEventListener('ended', handleEnded);

      fullVideo.muted = false;
      fullVideo.defaultMuted = false;
      fullVideo.volume = 1;
      fullVideo.removeAttribute('muted');

      const playPromise = fullVideo.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(function() {
          const resume = function() {
            fullVideo.play().catch(function() {});
            fullVideo.removeEventListener('pointerdown', resume);
          };
          fullVideo.addEventListener('pointerdown', resume);
        });
      }
    }

    function pauseOtherPreviews(activeCard) {
      sectionEl.querySelectorAll('.ugc-card').forEach(function(card) {
        if (card === activeCard) return;
        if (card.dataset.hasPreview !== 'true') return;
        const preview = card.querySelector('.ugc-card__video-preview');
        if (preview) {
          preview.pause();
          try {
            preview.currentTime = 0;
          } catch (error) {
            /* ignore seek issues */
          }
        }
        card.classList.add('ugc-card--preview-paused');
      });
    }

    function stopFullVideo() {
      if (!activeFullCard) {
        return;
      }
      const wrapper = activeFullCard.querySelector('.ugc-card__video-wrapper');
      const fullVideo = activeFullVideo;
      const previewVideo = activeFullCard.querySelector('.ugc-card__video-preview');

      if (fullObserver) {
        fullObserver.disconnect();
        fullObserver = null;
      }

      if (fullVideo) {
        if (fullVideo._ugcHandleEnded) {
          fullVideo.removeEventListener('ended', fullVideo._ugcHandleEnded);
          delete fullVideo._ugcHandleEnded;
        }
        fullVideo.pause();
        fullVideo.currentTime = 0;
      }

      if (wrapper) {
        wrapper.classList.remove('playing-full');
      }

      if (previewVideo) {
        requestAnimationFrame(function() {
          const promise = previewVideo.play();
          if (promise && typeof promise.then === 'function') {
            promise.catch(function() {});
          }
        });
      }

      sectionEl.classList.remove('ugc--full-playing');
      activeFullCard.classList.remove('ugc-card--active-full');
      activeFullCard.classList.remove('ugc-card--preview-paused');

      activeFullCard = null;
      activeFullVideo = null;

      removePause('full');
      momentumVelocity = 0;
      removePause('momentum');
      sectionEl.classList.remove('ugc--is-dragging');
      setTrackTouchAction(TOUCH_ACTION_DEFAULT);
      resumePreviewVideos();
      stopLoopIfIdle();
    }

    function resumePreviewVideos() {
      if (previewsHiddenByDocument) {
        return;
      }
      sectionEl.querySelectorAll('.ugc-card').forEach(function(card) {
        card.classList.remove('ugc-card--preview-paused');
        if (card.dataset.hasPreview !== 'true') return;
        const preview = card.querySelector('.ugc-card__video-preview');
        if (!preview) return;
        ensurePreviewSource(preview);
        if (!previewObserver) {
          loadPreview(preview);
          playPreviewWhenReady(preview);
        } else if (isVideoInViewport(preview)) {
          loadPreview(preview);
          playPreviewWhenReady(preview);
        }
      });
    }

    function startVisiblePreviewPlayback() {
      if (previewsHiddenByDocument) {
        return;
      }
      sectionEl.querySelectorAll('.ugc-card').forEach(function(card) {
        if (card.dataset.hasPreview !== 'true') return;
        const preview = card.querySelector('.ugc-card__video-preview');
        if (!preview) return;
        if (!previewObserver || isVideoInViewport(preview)) {
          ensurePreviewSource(preview);
          loadPreview(preview);
          playPreviewWhenReady(preview);
        }
      });
    }

    function queueInitialPreviewAutoplay() {
      if (previewsHiddenByDocument) {
        return;
      }
      requestAnimationFrame(function() {
        if (previewsHiddenByDocument) {
          return;
        }
        startVisiblePreviewPlayback();
      });
    }

    function playPreviewWhenReady(video) {
      if (!video) return;
      if (video.readyState >= 2) {
        requestPreviewPlay(video);
        return;
      }
      if (video.dataset.playOnReady === 'true') return;
      const handleReady = function() {
        video.removeEventListener('loadeddata', handleReady);
        video.removeEventListener('canplay', handleReady);
        delete video.dataset.playOnReady;
        requestPreviewPlay(video);
      };
      video.dataset.playOnReady = 'true';
      video.addEventListener('loadeddata', handleReady);
      video.addEventListener('canplay', handleReady);
    }

    function ensurePreviewSource(video) {
      if (!video || video.dataset.sourceAttached === 'true') return;
      var hevcSrc = video.dataset.previewHevc;
      var h264Src = video.dataset.previewH264;
      var sourceToUse = null;

      if (hevcSrc && SUPPORTS_HEVC) {
        sourceToUse = { src: hevcSrc, type: HEVC_MIME };
      } else if (h264Src && SUPPORTS_H264) {
        sourceToUse = { src: h264Src, type: H264_MIME };
      } else if (h264Src) {
        sourceToUse = { src: h264Src, type: H264_MIME };
      } else if (hevcSrc) {
        sourceToUse = { src: hevcSrc, type: HEVC_MIME };
      }

      if (!sourceToUse) return;

      const sourceEl = document.createElement('source');
      sourceEl.src = sourceToUse.src;
      sourceEl.type = sourceToUse.type;
      video.appendChild(sourceEl);
      video.dataset.sourceAttached = 'true';
    }

    function loadPreview(video) {
      if (!video || video.dataset.previewLoaded === 'true') return;
      video.load();
      video.dataset.previewLoaded = 'true';
    }

    function requestPreviewPlay(video) {
      if (!video) return;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(function() {
          if (video.dataset.previewResumeBound === 'true') return;
          const resume = function() {
            video.play().catch(function() {});
            video.removeEventListener('pointerdown', resume);
            delete video.dataset.previewResumeBound;
          };
          video.dataset.previewResumeBound = 'true';
          video.addEventListener('pointerdown', resume);
        });
      }
    }

    function ensureFullVideoSources(video) {
      if (!video || video.dataset.sourceAttached === 'true') return;
      var hevcSrc = video.dataset.fullHevc;
      var h264Src = video.dataset.fullH264;
      var sourceToUse = null;

      if (hevcSrc && SUPPORTS_HEVC) {
        sourceToUse = { src: hevcSrc, type: HEVC_MIME };
      } else if (h264Src && SUPPORTS_H264) {
        sourceToUse = { src: h264Src, type: H264_MIME };
      } else if (h264Src) {
        sourceToUse = { src: h264Src, type: H264_MIME };
      } else if (hevcSrc) {
        sourceToUse = { src: hevcSrc, type: HEVC_MIME };
      }

      if (!sourceToUse) return;

      const sourceEl = document.createElement('source');
      sourceEl.src = sourceToUse.src;
      sourceEl.type = sourceToUse.type;
      video.appendChild(sourceEl);
      video.dataset.sourceAttached = 'true';
    }

    function isVideoInViewport(video) {
      if (!video || !viewport) return false;
      const videoRect = video.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();
      const margin = 16;
      return (
        videoRect.right > viewportRect.left - margin &&
        videoRect.left < viewportRect.right + margin &&
        videoRect.bottom > viewportRect.top - margin &&
        videoRect.top < viewportRect.bottom + margin
      );
    }

    function handlePreviewIntersection(entries) {
      if (!isSectionVisible) {
        entries.forEach(function(entry) {
          if (entry.target && typeof entry.target.pause === 'function') {
            entry.target.pause();
          }
        });
        return;
      }
      if (previewsHiddenByDocument) {
        entries.forEach(function(entry) {
          if (entry.target && typeof entry.target.pause === 'function') {
            entry.target.pause();
          }
        });
        return;
      }
      entries.forEach(function(entry) {
        const video = entry.target;
        if (!video) return;
        const card = video.closest('.ugc-card');
        const stillVisible = entry.isIntersecting || entry.intersectionRatio > 0 || isVideoInViewport(video);
        if (!stillVisible) {
          video.pause();
          return;
        }
        if (card && card.classList.contains('ugc-card--preview-paused')) {
          video.pause();
          return;
        }
        ensurePreviewSource(video);
        loadPreview(video);
        playPreviewWhenReady(video);
      });
    }

    function setTrackTouchAction(value) {
      if (!track) return;
      track.style.touchAction = value;
      track.style.webkitTouchAction = value;
    }

    function getNow() {
      return (typeof performance !== 'undefined' && typeof performance.now === 'function')
        ? performance.now()
        : Date.now();
    }
  }

  document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('.ugc-slider').forEach(initUGCSlider);
  });

  document.addEventListener('shopify:section:load', function(event) {
    event.target.querySelectorAll('.ugc-slider').forEach(function(sectionEl) {
      sectionEl.dataset.initialized = '';
      initUGCSlider(sectionEl);
    });
  });

  document.addEventListener('shopify:section:unload', function(event) {
    event.target.querySelectorAll('.ugc-slider').forEach(function(sectionEl) {
      if (sectionEl._ugcCleanup) {
        sectionEl._ugcCleanup();
        delete sectionEl._ugcCleanup;
      }
      sectionEl.dataset.initialized = '';
    });
  });
})();